/**
 * Derived system facts — V108-3-4 / ADR-0086 §5, ADR-0085 §5 (migrations 609–610).
 *
 * Discovery derives three facts per AI system and stores each as a SUGGESTION with provenance
 * (`detected_by`, the derivation `run_id`, the evidence it rests on):
 *   - internet_reachable — from the system's linked boundary resources' `is_public`: any public
 *     → true; every linked resource known not public → false; otherwise nothing (absence proves
 *     nothing);
 *   - providers — model providers from cloud AI services (Bedrock, SageMaker, Vertex, Azure OpenAI
 *     resources and observed AI resources), SDK imports in the system's repositories and SBOM
 *     (incl. langchain provider packages) and AI gateways (usage rollups by provider);
 *   - environments — the environments its linked resources run in (tag, else connection).
 * A derivation NEVER writes the system. A person (owner / admin; the route checks) confirms —
 * that writes the fact with provenance (`facts_provenance_json`) — or dismisses; a dismissed
 * suggestion comes back only when its inputs change. A suggestion equal to the current answer is
 * not raised (and an open one is superseded).
 *
 * Pure of Cloudflare (check-imports). Every query is keyed by the caller's workspace id.
 */
import { purlToSdkAlias } from '../connectors/provider-signals.js';
import { generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { parseProviders, setSystemFacts } from './boundary-service.js';
import { CUSTOMER_ENVIRONMENTS, isCustomerEnvironment, resourceEnvironment } from './customer-environment-service.js';
// ---- provider vocabulary (shared with the onboarding intake; was web-only) --------------------
/** The model-provider keys a system's `providers` fact uses (labels for the "we found X" line). */
export const AI_PROVIDER_LABELS = Object.freeze({
    openai: 'OpenAI API', anthropic: 'Anthropic API', 'amazon-bedrock': 'Amazon Bedrock', 'amazon-sagemaker': 'Amazon SageMaker',
    'azure-openai': 'Azure OpenAI', 'google-vertex-ai': 'Google Vertex AI', 'google-gemini': 'Google Gemini API',
    mistral: 'Mistral', cohere: 'Cohere', 'self-hosted': 'Self-hosted model',
});
/** SDK package (`npm:` / `pypi:`) → provider key. Reviewed list, exact names (ADR-0039: no guessing). */
const SDK_PROVIDER = Object.freeze({
    'npm:openai': 'openai', 'pypi:openai': 'openai', 'npm:@ai-sdk/openai': 'openai', 'pypi:langchain-openai': 'openai', 'npm:@langchain/openai': 'openai',
    'npm:@anthropic-ai/sdk': 'anthropic', 'pypi:anthropic': 'anthropic', 'npm:@ai-sdk/anthropic': 'anthropic', 'pypi:langchain-anthropic': 'anthropic', 'npm:@langchain/anthropic': 'anthropic',
    'npm:@aws-sdk/client-bedrock-runtime': 'amazon-bedrock', 'npm:@aws-sdk/client-bedrock-agent-runtime': 'amazon-bedrock', 'npm:@ai-sdk/amazon-bedrock': 'amazon-bedrock',
    'pypi:langchain-aws': 'amazon-bedrock', 'npm:@langchain/aws': 'amazon-bedrock', 'npm:@anthropic-ai/bedrock-sdk': 'amazon-bedrock',
    'npm:@aws-sdk/client-sagemaker-runtime': 'amazon-sagemaker', 'pypi:sagemaker': 'amazon-sagemaker',
    'pypi:google-cloud-aiplatform': 'google-vertex-ai', 'npm:@google-cloud/vertexai': 'google-vertex-ai', 'npm:@ai-sdk/google-vertex': 'google-vertex-ai',
    'pypi:langchain-google-vertexai': 'google-vertex-ai', 'npm:@langchain/google-vertexai': 'google-vertex-ai', 'npm:@anthropic-ai/vertex-sdk': 'google-vertex-ai',
    'npm:@google/generative-ai': 'google-gemini', 'npm:@google/genai': 'google-gemini', 'pypi:google-generativeai': 'google-gemini', 'pypi:google-genai': 'google-gemini',
    'pypi:langchain-google-genai': 'google-gemini', 'npm:@langchain/google-genai': 'google-gemini', 'npm:@ai-sdk/google': 'google-gemini',
    'npm:@azure/openai': 'azure-openai', 'pypi:azure-ai-inference': 'azure-openai', 'npm:@ai-sdk/azure': 'azure-openai',
    'pypi:mistralai': 'mistral', 'npm:@mistralai/mistralai': 'mistral', 'pypi:langchain-mistralai': 'mistral', 'npm:@langchain/mistralai': 'mistral',
    'pypi:cohere': 'cohere', 'npm:cohere-ai': 'cohere', 'pypi:langchain-cohere': 'cohere', 'npm:@langchain/cohere': 'cohere',
    'pypi:torch': 'self-hosted', 'pypi:tensorflow': 'self-hosted', 'pypi:transformers': 'self-hosted', 'pypi:vllm': 'self-hosted',
});
/** Provider key for one SDK clue (`npm:openai`), else null. */
export function providerForSdk(sdk) {
    return SDK_PROVIDER[sdk.trim().toLowerCase()] ?? null;
}
/** Cloud resource type (`aws:bedrock:…`, `gcp:aiplatform.googleapis.com/…`, `azure:microsoft.cognitiveservices/accounts`) → provider key. */
export function providerForResourceType(resourceType) {
    const t = resourceType.trim().toLowerCase();
    if (t.startsWith('aws:bedrock:'))
        return 'amazon-bedrock';
    if (t.startsWith('aws:sagemaker:'))
        return 'amazon-sagemaker';
    if (t.startsWith('gcp:aiplatform.googleapis.com/'))
        return 'google-vertex-ai';
    if (t === 'azure:microsoft.cognitiveservices/accounts' || t.startsWith('azure:microsoft.cognitiveservices/accounts/'))
        return 'azure-openai';
    return null;
}
/** A registry provider key + access channel (observed AI resources, gateway usage) → provider key. */
export function providerForChannel(providerKey, channel) {
    const c = (channel ?? '').toLowerCase();
    if (c === 'bedrock')
        return 'amazon-bedrock';
    if (c === 'vertex')
        return 'google-vertex-ai';
    if (c === 'azure_openai')
        return 'azure-openai';
    if (c === 'sagemaker')
        return 'amazon-sagemaker';
    const p = (providerKey ?? '').toLowerCase();
    if (p === 'openai' || p === 'anthropic' || p === 'mistral' || p === 'cohere')
        return p;
    if (p === 'google' || p === 'gemini')
        return 'google-gemini';
    return null;
}
/**
 * The onboarding scan's indicators ("openai in requirements.txt", "langchain-anthropic in
 * pyproject.toml") → provider keys. Shared with the SPA (`providersFromIndicators`).
 */
export function providersFromIndicators(indicators) {
    const found = new Set();
    for (const i of indicators) {
        const lib = (i.split(' ')[0] ?? '').toLowerCase();
        if (!lib)
            continue;
        const hit = providerForSdk(`pypi:${lib}`) ?? providerForSdk(`npm:${lib}`);
        if (hit)
            found.add(hit);
    }
    return Object.keys(AI_PROVIDER_LABELS).filter((k) => found.has(k));
}
const sortU = (xs) => [...new Set(xs)].sort();
/** Derive every system's facts from what discovery holds (pure reads; writes nothing). */
export async function deriveSystemFacts(db, workspaceId) {
    const systems = (await db.query(`SELECT system_id, internet_reachable, providers_json, environments_json FROM ai_systems WHERE workspace_id = ? ORDER BY system_id`, [workspaceId])).results;
    if (!systems.length)
        return [];
    const envByConn = new Map((await db.query(`SELECT connector_id, environment FROM evidence_connectors WHERE workspace_id = ?`, [workspaceId])).results
        .map((r) => [r.connector_id, isCustomerEnvironment(r.environment) ? r.environment : 'production']));
    // Linked resources: boundary components of the system (and the resource suggestions linked to them).
    const comps = (await db.query(`SELECT component_id, system_id, is_public, source_connector_id FROM boundary_components WHERE workspace_id = ? AND system_id IS NOT NULL AND removed_at IS NULL`, [workspaceId])).results;
    const sugg = (await db.query(`SELECT suggestion_id, component_id, connector_id, resource_type, is_public, tags_json, environment FROM boundary_resource_suggestions WHERE workspace_id = ? AND gone_at IS NULL`, [workspaceId])).results;
    const suggByComponent = new Map();
    for (const s of sugg)
        if (s.component_id)
            suggByComponent.set(s.component_id, [...(suggByComponent.get(s.component_id) ?? []), s]);
    // Workspace-level AI sources (offered to every system, minus its "not used here" dismissals).
    const aiRes = (await db.query(`SELECT resource_id, provider_key, access_channel, kind FROM observed_ai_resources WHERE workspace_id = ? AND gone_at IS NULL`, [workspaceId])).results;
    const dismissed = new Set((await db.query(`SELECT system_id, resource_id FROM system_ai_resource_dismissals WHERE workspace_id = ?`, [workspaceId])).results.map((d) => `${d.system_id}|${d.resource_id}`));
    const gateway = (await db.query(`SELECT DISTINCT provider_key, access_channel FROM ai_usage_rollups WHERE workspace_id = ?`, [workspaceId]).catch(() => ({ results: [] }))).results;
    const awsAiResources = sugg.filter((s) => providerForResourceType(s.resource_type));
    // Per-system SDKs: SBOM components + repo-scan hits of the system's linked repositories.
    const comp = (await db.query(`SELECT system_id, purl_canonical FROM ai_system_components WHERE workspace_id = ? AND classification_status <> 'rejected'`, [workspaceId])).results;
    const repos = (await db.query(`SELECT system_id, repo_ref FROM ai_system_repos WHERE workspace_id = ?`, [workspaceId])).results;
    const repoSources = (await db.query(`SELECT ref_id, detail_json FROM infra_inventory_sources WHERE workspace_id = ? AND source_kind = 'repo_scan' AND gone_at IS NULL`, [workspaceId])).results;
    const sdkByRepo = new Map();
    for (const s of repoSources) {
        let d = {};
        try {
            d = JSON.parse(s.detail_json);
        }
        catch { /* skip */ }
        const repo = s.ref_id.replace(/^repo:/, '').toLowerCase();
        for (const h of d.hits ?? [])
            if (h.kind === 'sdk' && typeof h.value === 'string')
                sdkByRepo.set(repo, (sdkByRepo.get(repo) ?? new Set()).add(h.value));
    }
    const out = [];
    for (const sys of systems) {
        const mine = comps.filter((c) => c.system_id === sys.system_id);
        // internet_reachable
        const pub = [];
        for (const c of mine) {
            pub.push({ ref: `component:${c.component_id}`, v: c.is_public });
            for (const s of suggByComponent.get(c.component_id) ?? [])
                pub.push({ ref: `resource:${s.suggestion_id}`, v: s.is_public });
        }
        const known = pub.filter((p) => p.v === 0 || p.v === 1);
        if (known.some((p) => p.v === 1)) {
            out.push({ system_id: sys.system_id, fact_key: 'internet_reachable', value: true, detected_by: ['cloud_resources'],
                evidence: known.filter((p) => p.v === 1).slice(0, 20).map((p) => ({ detected_by: 'cloud_resources', ref: p.ref })) });
        }
        else if (pub.length && known.length === pub.length) {
            out.push({ system_id: sys.system_id, fact_key: 'internet_reachable', value: false, detected_by: ['cloud_resources'],
                evidence: known.slice(0, 20).map((p) => ({ detected_by: 'cloud_resources', ref: p.ref })) });
        }
        // providers
        const prov = new Map();
        const add = (k, e) => { if (k)
            prov.set(k, [...(prov.get(k) ?? []), e]); };
        for (const s of awsAiResources)
            add(providerForResourceType(s.resource_type), { detected_by: 'cloud_resources', ref: `resource:${s.suggestion_id}` });
        for (const r of aiRes)
            if (!dismissed.has(`${sys.system_id}|${r.resource_id}`))
                add(providerForChannel(r.provider_key, r.access_channel), { detected_by: 'ai_resources', ref: `ai_resource:${r.resource_id}` });
        for (const g of gateway)
            add(providerForChannel(g.provider_key, g.access_channel), { detected_by: 'ai_gateway', ref: `gateway:${g.provider_key ?? ''}/${g.access_channel ?? ''}` });
        for (const c of comp.filter((x) => x.system_id === sys.system_id)) {
            const alias = purlToSdkAlias(c.purl_canonical);
            if (alias)
                add(providerForSdk(alias), { detected_by: 'sbom', ref: `purl:${c.purl_canonical.replace(/@[^@/]*$/, '')}` });
        }
        for (const r of repos.filter((x) => x.system_id === sys.system_id)) {
            for (const sdk of sdkByRepo.get(r.repo_ref.toLowerCase()) ?? [])
                add(providerForSdk(sdk), { detected_by: 'sdk_imports', ref: `repo:${r.repo_ref}#${sdk}` });
        }
        if (prov.size) {
            const evidence = [...prov.values()].flat();
            out.push({ system_id: sys.system_id, fact_key: 'providers', value: sortU(prov.keys()), detected_by: sortU(evidence.map((e) => e.detected_by)), evidence: evidence.slice(0, 50) });
        }
        // environments
        const envs = new Map();
        for (const c of mine) {
            for (const s of suggByComponent.get(c.component_id) ?? []) {
                let tags = {};
                try {
                    tags = JSON.parse(s.tags_json);
                }
                catch { /* none */ }
                const env = isCustomerEnvironment(s.environment) ? s.environment : resourceEnvironment(tags, envByConn.get(s.connector_id) ?? 'production').environment;
                envs.set(env, [...(envs.get(env) ?? []), { detected_by: 'tags', ref: `resource:${s.suggestion_id}` }]);
            }
        }
        if (envs.size) {
            const evidence = [...envs.values()].flat();
            out.push({ system_id: sys.system_id, fact_key: 'environments', value: CUSTOMER_ENVIRONMENTS.filter((e) => envs.has(e)), detected_by: ['tags'], evidence: evidence.slice(0, 50) });
        }
    }
    return out;
}
function currentValue(sys, key) {
    if (key === 'internet_reachable')
        return sys.internet_reachable === null ? null : sys.internet_reachable === 1;
    if (key === 'providers')
        return parseProviders(sys.providers_json);
    let v = [];
    try {
        v = JSON.parse(sys.environments_json ?? '[]');
    }
    catch {
        v = [];
    }
    return Array.isArray(v) ? CUSTOMER_ENVIRONMENTS.filter((e) => v.includes(e)) : [];
}
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/**
 * Derive and record suggestions (idempotent: same inputs → no new row). Never writes ai_systems.
 * A value equal to the system's current answer raises nothing and supersedes an open one; a
 * dismissed suggestion with the same inputs is not raised again.
 */
export async function refreshFactSuggestions(db, workspaceId, opts = {}) {
    const nowIso = (opts.now ?? new Date()).toISOString();
    const runId = opts.runId ?? generateRandomId('drv');
    const newId = opts.newId ?? (() => generateRandomId('sfs'));
    const out = { raised: 0, superseded: 0, unchanged: 0 };
    const facts = await deriveSystemFacts(db, workspaceId);
    const systems = new Map((await db.query(`SELECT system_id, internet_reachable, providers_json, environments_json FROM ai_systems WHERE workspace_id = ?`, [workspaceId])).results.map((s) => [s.system_id, s]));
    const open = (await db.query(`SELECT * FROM system_fact_suggestions WHERE workspace_id = ? AND status = 'suggested'`, [workspaceId])).results;
    const openByKey = new Map(open.map((o) => [`${o.system_id}|${o.fact_key}`, o]));
    const seen = new Set();
    for (const f of facts) {
        const key = `${f.system_id}|${f.fact_key}`;
        seen.add(key);
        const sys = systems.get(f.system_id);
        const cur = currentValue(sys, f.fact_key);
        const existing = openByKey.get(key);
        // Already the answer (a person's or a confirmed one): nothing to ask.
        if (same(cur, f.value)) {
            if (existing) {
                await supersede(db, workspaceId, existing.suggestion_id);
                out.superseded++;
            }
            continue;
        }
        const sha = await sha256Hex(JSON.stringify([f.fact_key, f.value, cur]));
        if (existing?.inputs_sha256 === sha) {
            out.unchanged++;
            continue;
        }
        const wasDismissed = (await db.query(`SELECT 1 AS ok FROM system_fact_suggestions WHERE workspace_id = ? AND system_id = ? AND fact_key = ? AND status = 'dismissed' AND inputs_sha256 = ? LIMIT 1`, [workspaceId, f.system_id, f.fact_key, sha])).results.length > 0;
        if (existing) {
            await supersede(db, workspaceId, existing.suggestion_id);
            out.superseded++;
        }
        if (wasDismissed) {
            out.unchanged++;
            continue;
        }
        await db.execute(`INSERT INTO system_fact_suggestions (suggestion_id, workspace_id, system_id, fact_key, value_json, current_json, detected_by, run_id, evidence_json, inputs_sha256, status, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'suggested', ?)`, [newId(), workspaceId, f.system_id, f.fact_key, JSON.stringify(f.value), cur === null ? null : JSON.stringify(cur), f.detected_by.join(','), runId,
            JSON.stringify(f.evidence), sha, nowIso]);
        out.raised++;
    }
    // A fact no longer derived: its open suggestion is superseded.
    for (const o of open)
        if (!seen.has(`${o.system_id}|${o.fact_key}`)) {
            await supersede(db, workspaceId, o.suggestion_id);
            out.superseded++;
        }
    return out;
}
async function supersede(db, workspaceId, suggestionId) {
    await db.execute(`UPDATE system_fact_suggestions SET status = 'superseded' WHERE suggestion_id = ? AND workspace_id = ? AND status = 'suggested'`, [suggestionId, workspaceId]);
}
export async function listFactSuggestions(db, workspaceId, opts = {}) {
    const where = ['workspace_id = ?', 'status = ?'];
    const params = [workspaceId, opts.status ?? 'suggested'];
    if (opts.systemId) {
        where.push('system_id = ?');
        params.push(opts.systemId);
    }
    return (await db.query(`SELECT * FROM system_fact_suggestions WHERE ${where.join(' AND ')} ORDER BY system_id, fact_key LIMIT 500`, params)).results;
}
export class FactSuggestionError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FactSuggestionError';
    }
}
async function openSuggestion(db, workspaceId, suggestionId) {
    const r = (await db.query(`SELECT * FROM system_fact_suggestions WHERE suggestion_id = ? AND workspace_id = ? LIMIT 1`, [suggestionId, workspaceId])).results[0];
    if (!r)
        throw new FactSuggestionError('not_found', 'Suggestion not found.');
    if (r.status !== 'suggested')
        throw new FactSuggestionError('conflict', `This suggestion was already ${r.status}.`);
    return r;
}
/**
 * A person confirms "we found X": writes the fact on the system with provenance. The caller
 * authorizes (owner / admin) and audit-logs. Returns the written value.
 */
export async function confirmFactSuggestion(db, input) {
    const s = await openSuggestion(db, input.workspace_id, input.suggestion_id);
    const at = (input.now ?? new Date()).toISOString();
    const value = JSON.parse(s.value_json);
    if (s.fact_key === 'internet_reachable')
        await setSystemFacts(db, { workspace_id: input.workspace_id, system_id: s.system_id, internet_reachable: value, now: input.now });
    else if (s.fact_key === 'providers')
        await setSystemFacts(db, { workspace_id: input.workspace_id, system_id: s.system_id, providers: value, now: input.now });
    else {
        await db.execute(`UPDATE ai_systems SET environments_json = ?, updated_at = ? WHERE system_id = ? AND workspace_id = ?`, [JSON.stringify(value.filter(isCustomerEnvironment)), at, s.system_id, input.workspace_id]);
    }
    const prov = (await db.query(`SELECT facts_provenance_json FROM ai_systems WHERE system_id = ? AND workspace_id = ?`, [s.system_id, input.workspace_id])).results[0];
    let p = {};
    try {
        p = JSON.parse(prov?.facts_provenance_json ?? '{}');
    }
    catch {
        p = {};
    }
    p[s.fact_key] = { source: 'derived', suggestion_id: s.suggestion_id, detected_by: s.detected_by, run_id: s.run_id, confirmed_by: input.actor_user_id, at };
    await db.execute(`UPDATE ai_systems SET facts_provenance_json = ? WHERE system_id = ? AND workspace_id = ?`, [JSON.stringify(p), s.system_id, input.workspace_id]);
    await db.execute(`UPDATE system_fact_suggestions SET status = 'confirmed', decided_by = ?, decided_at = ? WHERE suggestion_id = ? AND workspace_id = ? AND status = 'suggested'`, [input.actor_user_id, at, s.suggestion_id, input.workspace_id]);
    return { suggestion: { ...s, status: 'confirmed', decided_by: input.actor_user_id, decided_at: at }, value };
}
/** A person says "not right": the suggestion closes and returns only when its inputs change. */
export async function dismissFactSuggestion(db, input) {
    const s = await openSuggestion(db, input.workspace_id, input.suggestion_id);
    const at = (input.now ?? new Date()).toISOString();
    await db.execute(`UPDATE system_fact_suggestions SET status = 'dismissed', decided_by = ?, decided_at = ? WHERE suggestion_id = ? AND workspace_id = ? AND status = 'suggested'`, [input.actor_user_id, at, s.suggestion_id, input.workspace_id]);
    return { ...s, status: 'dismissed', decided_by: input.actor_user_id, decided_at: at };
}
//# sourceMappingURL=derived-facts-service.js.map